<script setup lang="ts">
import { storeToRefs } from 'pinia'

import Modal from '~/components/shared/modal.vue'
import { BtnGhost, BtnOrange } from '~/components/ui/Buttons'
import VText from '~/components/ui/VText.vue'

import { useSegmentTrack } from '~/composables/Segment/useSegmentTrack'
import { useGetMediaUrl } from '~/composables/useGetMediaUrl'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useSnackbarStore } from '~/stores/snackbar'
import { useUserStore } from '~/stores/user'
import { useUserInfluencerStore } from '~/stores/userInfluencer'

import { getLoginLinkFromEmail } from '~/helpers/Signup/GetLoginLinkFromEmail'

import { provideAuthResendActivationLinkEmail } from '~/api-core/Account/AuthUser'

const { coreFetch } = useProvideCoreFetch()
const segmentTrack = useSegmentTrack()

const display = ref(false)
const loading = ref(false)

const { entity: USER_ENTITY, id: CURATOR_ID } = storeToRefs(
  useUserInfluencerStore(),
)
const { email: USER_EMAIL } = storeToRefs(useUserStore())
const { CREATE: CREATE_SNACKBAR } = useSnackbarStore()

const { query: routeQuery } = useRoute()
const { getMediaUrl } = useGetMediaUrl()
const hasCoolEmail = computed<false | string>(() => {
  const query = routeQuery.fs

  if (typeof query === 'string') return getLoginLinkFromEmail(query)
  else return false
})

onMounted(() => {
  if (!window.localStorage.getItem('needsWarmWelcome')) return

  display.value = true
  window.localStorage.removeItem('needsWarmWelcome')
})

async function sendAgain(): Promise<void> {
  if (loading.value) return

  loading.value = true
  try {
    const postResendActivationLinkEmail =
      provideAuthResendActivationLinkEmail(coreFetch)
    await postResendActivationLinkEmail(USER_EMAIL.value)
    segmentTrack('Curator Email Confirmation Modal - Send Again Clicked', {
      curator_id: CURATOR_ID.value,
    })
    CREATE_SNACKBAR({
      props: {
        text: 'Yeah!',
        icon: 'fas fa-check',
      },
    })
  } catch (_) {
    // do nothing
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <Modal
    v-model="display"
    :title="
      $t('layout.confirmations.influencerSignup00', { name: USER_ENTITY })
    "
  >
    <div class="tw-grid tw-grid-cols-1 tw-gap-10 tw-pb-6">
      <div class="content">
        <VText v-once cfg="sans/16/regular">{{
          $t('layout.confirmations.influencerSignup01')
        }}</VText>
        <VText v-once cfg="sans/16/regular">{{ USER_EMAIL }}</VText>
        <br />
        <VText v-once cfg="sans/16/regular">{{
          $t('layout.confirmations.influencerSignup02')
        }}</VText>
      </div>
      <div class="tw-flex tw-items-center tw-justify-center tw-gap-4">
        <BtnGhost @click="sendAgain">
          {{ $t('layout.confirmations.influencerSignup04') }}
        </BtnGhost>
        <a v-if="hasCoolEmail" :href="hasCoolEmail">
          <BtnOrange :loading="loading">
            {{ $t('layout.confirmations.influencerSignup03') }}
          </BtnOrange>
        </a>
      </div>
      <div class="imgHolder">
        <img :src="getMediaUrl('Check_ur_inbox_b0ss.png')" />
      </div>
    </div>
  </Modal>
</template>

<style scoped>
.imgHolder {
  width: 100%;
  overflow: hidden;
  position: relative;
}

.imgHolder > img {
  width: 100%;
  height: 160px;
  object-fit: contain;
  object-position: center;
}
</style>
